
import { Component, Mixins, Watch } from 'vue-property-decorator'
import IconsMixin from '@/mixins/IconsMixin.vue'
import AuthMixin from '@/mixins/AuthMixin.vue'
import LabeledValue from '@/components/LabeledValue.vue'
import LoadingPage from '@/components/LoadingPage.vue'
import OrderListItem from '@/components/Order/OrderListItem.vue'
import moment from 'moment'
import {
  IEODNotificationSettings,
  Order,
  OrderCollection,
  UserSetting,
  UserSettingCollection
} from '@/firestore'

interface GenericItemsEntry {
  description: string,
  quantity: string
}

interface RejectedItems {
  description: string,
  quantity: string,
  receivedQuantity: string
}

type DaysOfTheWeek = [boolean, boolean, boolean, boolean, boolean, boolean, boolean]

@Component({
  components: {
    OrderListItem,
    LoadingPage,
    LabeledValue
  }
})
export default class EODReportDetail extends Mixins(IconsMixin, AuthMixin) {
  public loaded: boolean = false
  public reportStartDate: string = moment().format('YYYY-MM-DD')
  public initialDate: string = moment().format('YYYY-MM-DD')
  public menu: boolean = false
  public orderedItems: GenericItemsEntry[] = []
  public acceptedItems: GenericItemsEntry[] = []
  public rejectedItems: RejectedItems[] = []
  public backOrderedItems: GenericItemsEntry[] = []
  public headers = [
    {text: 'Product Name', value: 'description'},
    {text: 'Quantity', value: 'quantity'}
  ]
  public headersForRejectedItemsTable = [
    {text: 'Product Name', value: 'description'},
    {text: 'Quantity', value: 'quantity'},
    {text: 'Expected Quantity', value: 'quantity'},
    {text: 'Rejected Quantity', value: 'receivedQuantity'}
  ]
  public headersForBackOrderedItemsTable = [
    {text: 'Product Name', value: 'description'},
    {text: 'Quantity', value: 'quantity'}
  ]

  public userSetting: UserSetting = {} as UserSetting
  public weekdays: DaysOfTheWeek = [false, false, false, false, false, false, false]

  //METHODS
  public async mounted() {
    this.loaded = false
    if (this.$route.params) {
      this.reportStartDate = moment(this.$route.params.date).format('YYYY-MM-DD')
      this.initialDate = this.reportStartDate
    }

    await this.queryFunction()
    const userSettingCollection = new UserSettingCollection((await this.$firebase).firestore())
    const userSetting = await userSettingCollection.getUserSettingByUserId(this.user.uid)

    if (userSetting) {
      this.userSetting = userSetting
      if (this.userSetting.eodNotificationSettings) {
        if (typeof this.userSetting.eodNotificationSettings[this.userCompany.id] === 'number') {
          this.weekdays = this.toTuple(this.userSetting.eodNotificationSettings[this.userCompany.id])
        } else {
          this.userSetting.eodNotificationSettings[this.userCompany.id] = 0
        }
      } else {
        this.userSetting.eodNotificationSettings = {[this.userCompany.id]: 0}
      }
    } else {
      this.userSetting = new UserSetting(this.user.uid)
      this.userSetting.eodNotificationSettings = {[this.userCompany.id]: 0}
    }
    this.loaded = true
  }

  @Watch('weekdays', {immediate: true, deep: true})
  public async updateUserSetting() {
    if (
        !this.userSetting
        || !this.userSetting.userId
        || !this.userSetting.id
        || (
            this.userSetting.eodNotificationSettings
            && this.userSetting.eodNotificationSettings[this.userCompany.id]
            && this.userSetting.eodNotificationSettings[this.userCompany.id] === this.toDecimal([...this.weekdays] as DaysOfTheWeek)
        )
    ) {
      return
    }

    const userSettingCollection = new UserSettingCollection((await this.$firebase).firestore())
    this.userSetting.eodNotificationSettings = {[this.userCompany.id]: this.toDecimal([...this.weekdays] as DaysOfTheWeek)} as IEODNotificationSettings

    await userSettingCollection.save(this.userSetting)
  }

  public clearDate() {
    this.reportStartDate = moment().format('YYYY-MM-DD')
    this.updateURL()
  }

  public updateURL() {
    if (this.reportStartDate && this.initialDate !== this.reportStartDate) {
      this.$router.push({name: 'report-eod-detail', params: {date: this.reportStartDate}})
      this.initialDate = this.reportStartDate
      this.queryFunction()
    }
  }

  public toDecimal(tuple: DaysOfTheWeek): number {
    return parseInt(tuple.reverse().map(val => val ? 1 : 0).join(''), 2)
  }

  public toTuple(decimal: number): DaysOfTheWeek {
    if (decimal < 0 || decimal > 127 || decimal % 1 !== 0) {
      throw new Error(`${decimal} does not fit in a 7 byte chunck`)
    }
    return decimal.toString(2)
        .padStart(7, '0')
        .split('')
        .reverse()
        .map(val => val === '1') as DaysOfTheWeek
  }

  public async queryFunction() {
    this.orderedItems = []
    this.acceptedItems = []
    this.rejectedItems = []
    this.backOrderedItems = []
    const orderArrayTest = []
    //this is to use the date that is selected
    const date = moment(this.reportStartDate, 'YYYY-MM-DD')
    const endOfDay = date.endOf('day').toISOString()
    const startOfDay = date.startOf('day').toISOString()
    const orderCollection = new OrderCollection((await this.$firebase).firestore())
    //for ordered items when the company is a restaurant
    const helper = await orderCollection.listApprovedOrdersByRestaurantIdAndDatePlacedRange(this.userCompany.id, startOfDay, endOfDay)
    helper.forEach(order => {
      order.orderEntries.forEach(entry => {
        this.orderedItems.push({
          description: entry.description,
          quantity: `${entry.quantity} ${entry.quantityType || ''}`
        })
      })
    })
    //for accepted and rejected items when the company is a vendor
    const acceptedItemsHelper = await orderCollection.listVendorOrdersByRestaurantIdandCheckedInRange(this.userCompany.id, startOfDay, endOfDay)
    acceptedItemsHelper.forEach(order => {
      order.orderEntries.forEach(entry => {
        if (entry.status !== 'rejected') {
          this.acceptedItems.push({
                description: entry.description,
                quantity: `${entry.quantity} ${entry.quantityType || ''}`
              }
          )
        } else {
          this.rejectedItems.push({
            description: entry.description,
            quantity: `${entry.quantity} ${entry.quantityType || ''}`,
            receivedQuantity: `${entry.quantity} ${entry.quantityType || ''}`
          })
        }
      })
    })
    // for backorders
    const vendorOrders = await orderCollection.listVendorOrdersByRestaurantIdandCheckedInRange(this.userCompany.id, startOfDay, endOfDay)
    const backOrders = (
        await Promise.all(
            vendorOrders.map(order =>
                orderCollection.listOrdersByOriginalOrderIdAndRestaurantId(order.id, this.userCompany.id)
            )
        )
    ).reduce((acc, val) => acc.concat(val), [] as Order[])
    backOrders.forEach(order => {
      order.orderEntries.forEach(entry => {
        this.backOrderedItems.push({
          description: entry.description,
          quantity: `${entry.quantity} ${entry.quantityType || ''}`
        })
      })
    })
  }
}
